/**
 * UnifiedDashboardSkeleton - Single loading state for entire dashboard
 *
 * CANON Compliance (5.3, 5.4):
 * - ONE skeleton shown from auth verification to content ready
 * - No transitions between multiple skeleton screens
 * - Professional, stable appearance
 * - Skeleton shape MUST match final content shape (no CLS)
 *
 * This skeleton is shown instead of:
 * - "Verifying authentication..." screen
 * - DashboardSkeleton
 * - WalletPageSkeleton / SendPageSkeleton / ReceivePageSkeleton
 *
 * User sees THIS ONE skeleton until everything is ready.
 */
import { cn } from "@/lib/utils";
import { UserSidebarSkeleton } from "@/components/user/layouts/UserSidebarSkeleton";
import { PageSkeleton } from "./SkeletonLayouts";

interface UnifiedDashboardSkeletonProps {
  /**
   * Include sidebar in skeleton layout (for full-page loading states)
   * @default true
   */
  withSidebar?: boolean;
}

/**
 * UnifiedDashboardSkeleton
 *
 * The ONE skeleton shown from initial page load until content is ready.
 * Matches exact layout of real dashboard: sidebar + content area.
 *
 * @param withSidebar - Include sidebar skeleton (default: true for full layout match)
 */
export function UnifiedDashboardSkeleton({ withSidebar = true }: UnifiedDashboardSkeletonProps = {}) {
  const contentSkeleton = <PageSkeleton variant="wallet" showHeader showActions />;

  if (!withSidebar) {
    return contentSkeleton;
  }

  return (
    <div className="flex min-h-screen w-full bg-background">
      {/* Desktop Sidebar Skeleton - matches UserLayout structure */}
      <UserSidebarSkeleton className="hidden lg:flex" />

      {/* Content Area */}
      <div className="flex flex-1 flex-col p-6 lg:p-8">
        {contentSkeleton}
      </div>
    </div>
  );
}

/**
 * UnifiedDashboardSkeletonCompact
 *
 * Content-only skeleton (no sidebar) for use inside existing layouts
 */
export function UnifiedDashboardSkeletonCompact() {
  return <PageSkeleton variant="wallet" showHeader showActions />;
}
